import type {
  FeedbackHubConfig,
  RemoteConfig,
  FeedbackCategory,
  FeedbackSeverity,
  FeedbackStatus,
  FeedbackPayload,
  CaptureType,
  FeedbackSummary,
  FeedbackComment,
} from './types';
import { DiagnosticsCapture } from './diagnostics';
import { captureScreenshot } from './screenshot';
import { AnnotationOverlay } from './annotation';
import { ScreenRecorder } from './recorder';
import { ApiClient } from './api-client';
import { getTesterToken } from './tester-token';
import { buildV2FormModal } from './feedback-ui-v2';
import { buildV3BotModal } from './feedback-ui-v3';
import { checkFeedbackQuality, renderQualityGateModal, dismissQualityGateModal, renderInlineQualityGate } from './quality-gates';
import { escapeHtml } from './dom-utils';
import {
  buildCategoryOptionsHtml,
  FEEDBACK_CATEGORIES,
  FEEDBACK_CATEGORY_ICON_BY_VALUE,
  isFeedbackCategory,
} from './feedback-categories';
import { buildSeverityOptionsHtml } from './feedback-severity';
import { captureSystemInfo } from './system-capture';
import { getFeedbackStatusColor, getFeedbackStatusLabel } from './feedback-status';

type ViewState = 'closed' | 'form' | 'recording' | 'success' | 'email-prompt' | 'my-feedback' | 'thread';

interface PendingFeedbackData {
  category: FeedbackCategory;
  title: string;
  description: string;
  expectedBehavior?: string;
  stepsToReproduce?: string;
  successCriteria?: string;
  contextualInfo?: string;
}

interface WidgetSessionState {
  schemaVersion: number;
  appId: string;
  sessionId?: string;
  panelState: 'open' | 'minimized' | 'closed';
  formVersion: 'v1' | 'v2' | 'v3' | 'unknown';
  savedAt: number;
}

/**
 * Core feedback UI controller.
 * Manages the floating button, modal, capture flow, form, and "My Feedback" panel.
 */
export class FeedbackUI {
  private config: FeedbackHubConfig;
  private remoteConfig: RemoteConfig | null = null;
  private diagnostics: DiagnosticsCapture;
  private api: ApiClient;
  private recorder: ScreenRecorder;
  private testerToken: string;
  private storedEmail: string | null = null;

  private static readonly EMAIL_STORAGE_KEY = 'fh_user_email';
  private static readonly FEEDBACK_FILTERS_STORAGE_KEY = 'fh_feedback_filters';
  private static readonly SESSION_STATE_KEY = 'fh-widget-state';

  // DOM refs
  private root: HTMLDivElement | null = null;
  private triggerBtn: HTMLButtonElement | null = null;
  private backdrop: HTMLDivElement | null = null;
  private modal: HTMLDivElement | null = null;
  private recordingBar: HTMLDivElement | null = null;

  // Capture state
  private capturedBlob: Blob | null = null;
  private capturedType: CaptureType = 'none';
  private viewState: ViewState = 'closed';
  private sessionId: string | null = null;

  // My Feedback state
  private myFeedbackList: FeedbackSummary[] = [];
  private myFeedbackApps: Array<{ id: string; name: string }> = [];
  private selectedAppIds: Set<string> = new Set();
  private selectedStatuses: Set<FeedbackStatus> = new Set();
  private feedbackPollingInterval: NodeJS.Timeout | null = null;
  private feedbackPollingPausedByVisibility = false;
  private activeThreadId: string | null = null;
  private activeThreadComments: FeedbackComment[] = [];
  private forceStandardForm = false;

  constructor(config: FeedbackHubConfig, diagnostics: DiagnosticsCapture) {
    this.config = config;
    this.diagnostics = diagnostics;
    this.api = new ApiClient(
      config.apiUrl ?? 'https://feedbackhub-api.azurewebsites.net/api',
      config.apiKey,
    );
    this.recorder = new ScreenRecorder(config.maxRecordingSeconds ?? 60);
    this.testerToken = getTesterToken();
    this.storedEmail = localStorage.getItem(FeedbackUI.EMAIL_STORAGE_KEY);

    // Recorder callbacks
    this.recorder.onStart = () => this.showRecordingBar();
    this.recorder.onStop = (blob) => {
      this.hideRecordingBar();
      this.capturedBlob = blob;
      this.capturedType = 'video';
      this.setView('form');
    };
    this.recorder.onError = (err) => {
      this.hideRecordingBar();
      console.warn('[FeedbackHub] Recording error:', err.message);
    };
    this.recorder.onTick = (secs) => this.updateRecordingTime(secs);
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────

  async mount(): Promise<void> {
    // Create the shadow root container
    this.root = document.createElement('div');
    this.root.id = 'fh-widget-root';
    document.body.appendChild(this.root);

    // Fetch remote config (non-blocking — use defaults if it fails)
    this.fetchRemoteConfig();

    this.renderTriggerButton();
  }

  destroy(): void {
    this.root?.remove();
    this.root = null;
  }

  private async fetchRemoteConfig(): Promise<void> {
    try {
      this.remoteConfig = await this.api.getConfig();
      // Apply remote theme color
      if (this.remoteConfig.themeColor && this.root) {
        this.root.style.setProperty('--fh-primary', this.remoteConfig.themeColor);
      }
    } catch {
      // Remote config fetch failed — proceed with local config defaults
    }
  }

  // ─── Resolved config (remote overrides local) ────────────────────────

  private get position(): string {
    return this.remoteConfig?.buttonPosition ?? this.config.buttonPosition ?? 'bottom-right';
  }

  private get screenshotEnabled(): boolean {
    return this.remoteConfig?.enableScreenshot ?? this.config.enableScreenshot ?? true;
  }

  private get videoEnabled(): boolean {
    return (
      (this.remoteConfig?.enableVideoRecording ?? this.config.enableVideoRecording ?? true) &&
      this.recorder.isSupported
    );
  }

  private get myFeedbackEnabled(): boolean {
    return this.remoteConfig?.enableMyFeedback ?? this.config.enableMyFeedback ?? true;
  }

  private get emailCaptureEnabled(): boolean {
    return this.remoteConfig?.enableEmailCapture ?? true;
  }

  private get feedbackFormVersion(): 'v1' | 'v2' | 'v3' {
    if (this.forceStandardForm) {
      return 'v2';
    }
    return this.remoteConfig?.feedbackFormVersion ?? 'v1';
  }

  private get enabledCategories(): FeedbackCategory[] {
    const remote = this.remoteConfig?.categories;
    if (!remote || remote.length === 0) {
      return [...FEEDBACK_CATEGORIES];
    }

    const filtered = remote.filter(isFeedbackCategory);
    return filtered.length > 0 ? filtered : [...FEEDBACK_CATEGORIES];
  }

  // ─── View management ──────────────────────────────────────────────────

  private setView(state: ViewState): void {
    this.viewState = state;
    this.clearModal();

    switch (state) {
      case 'closed':
        this.backdrop?.remove();
        this.backdrop = null;
        this.stopFeedbackPolling();
        this.clearSessionState();
        break;
      case 'form':
        // v3 conversational form is non-modal and persistent; v1/v2 use a backdrop
        if (this.feedbackFormVersion !== 'v3') {
          this.showBackdrop();
        }
        this.renderFormModal();
        // Only attempt restoration if we're in a state where restoration makes sense
        // (i.e., if sessionStorage has data for this app)
        if (this.feedbackFormVersion === 'v3') {
          const storedState = this.loadSessionState();
          if (storedState) {
            this.restoreSessionState();
          }
        }
        this.saveSessionState();
        this.updateMyFeedbackBadge();
        this.startFeedbackPolling(); // Start polling to show badge
        break;
      case 'success':
        this.showBackdrop();
        this.renderSuccessModal();
        this.updateMyFeedbackBadge();
        this.startFeedbackPolling();
        break;
      case 'my-feedback':
        this.showBackdrop();
        this.renderMyFeedbackModal();
        this.startFeedbackPolling();
        break;
      case 'email-prompt':
        this.showBackdrop();
        this.renderEmailPromptModal();
        break;
      case 'thread':
        this.showBackdrop();
        this.renderThreadModal();
        break;
      case 'recording':
        // Recording view — modal is hidden, recording bar is shown
        break;
    }
  }

  // ─── Trigger button ───────────────────────────────────────────────────

  private renderTriggerButton(): void {
    this.triggerBtn = document.createElement('button');
    this.triggerBtn.className = `fh-trigger-btn ${this.position}`;
    this.triggerBtn.innerHTML = `<svg viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/><path d="M7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z"/></svg>`;
    this.triggerBtn.title = 'Send Feedback';

    this.triggerBtn.onclick = () => {
      if (this.viewState === 'closed') {
        this.forceStandardForm = false;
        this.capturedBlob = null;
        this.capturedType = 'none';
        this.setView('form');
      } else {
        this.setView('closed');
      }
    };

    this.root!.appendChild(this.triggerBtn);
  }

  // ─── Backdrop ─────────────────────────────────────────────────────────

  private showBackdrop(): void {
    if (this.backdrop) return;
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'fh-backdrop';
    this.backdrop.onclick = () => this.setView('closed');
    this.root!.appendChild(this.backdrop);
  }

  private clearModal(): void {
    this.modal?.remove();
    this.modal = null;
  }

  private toggleMinimize(): void {
    if (!this.modal) return;

    const isMinimized = this.modal.classList.contains('minimized');

    if (isMinimized) {
      // Restore: remove minimized class and show restore button
      this.modal.classList.remove('minimized');
      const header = this.modal.querySelector('.fh-modal-header');
      if (header) {
        const minimizeBtn = header.querySelector('.fh-minimize-btn');
        const restoreBtn = header.querySelector('.fh-restore-btn');
        if (minimizeBtn) minimizeBtn.style.display = 'block';
        if (restoreBtn) restoreBtn.remove();
      }
    } else {
      // Minimize: add minimized class and show restore button
      this.modal.classList.add('minimized');
      const header = this.modal.querySelector('.fh-modal-header');
      if (header) {
        const minimizeBtn = header.querySelector('.fh-minimize-btn');
        if (minimizeBtn) minimizeBtn.style.display = 'none';

        // Create and add restore button
        const restoreBtn = document.createElement('button');
        restoreBtn.className = 'fh-restore-btn';
        restoreBtn.title = 'Restore';
        restoreBtn.style.fontSize = '16px';
        restoreBtn.innerHTML = '+';
        restoreBtn.addEventListener('click', () => this.toggleMinimize());

        const buttonGroup = header.querySelector('div');
        if (buttonGroup) buttonGroup.insertBefore(restoreBtn, buttonGroup.firstChild);
      }
    }

    this.saveSessionState();
  }

  private saveSessionState(): void {
    // Only save for v3 form (conversational widget)
    if (this.feedbackFormVersion !== 'v3') return;

    const panelState = this.modal?.classList.contains('minimized') ? 'minimized' : 'open';

    const state: WidgetSessionState = {
      schemaVersion: 1,
      appId: this.config.appId,
      sessionId: this.sessionId || undefined,
      panelState: this.viewState === 'closed' ? 'closed' : panelState,
      formVersion: 'v3',
      savedAt: Date.now(),
    };

    try {
      sessionStorage.setItem(FeedbackUI.SESSION_STATE_KEY, JSON.stringify(state));
    } catch (err) {
      // Silently fail if sessionStorage is unavailable
      console.warn('[FeedbackHub] Failed to save widget state:', err);
    }
  }

  private loadSessionState(): WidgetSessionState | null {
    try {
      const stored = sessionStorage.getItem(FeedbackUI.SESSION_STATE_KEY);
      if (!stored) return null;

      const state: WidgetSessionState = JSON.parse(stored);

      // Validate schema version
      if (state.schemaVersion !== 1) return null;

      // Validate appId matches
      if (state.appId !== this.config.appId) return null;

      return state;
    } catch (err) {
      // Silently fail if parsing fails
      return null;
    }
  }

  private clearSessionState(): void {
    try {
      sessionStorage.removeItem(FeedbackUI.SESSION_STATE_KEY);
    } catch (err) {
      // Silently fail
    }
  }

  private restoreSessionState(): void {
    // Only attempt restoration for v3 form
    if (this.feedbackFormVersion !== 'v3') return;

    // Load stored session state first
    const storedState = this.loadSessionState();

    // If no stored state, nothing to restore
    if (!storedState) return;

    // Check for URL parameter override
    const params = new URLSearchParams(window.location.search);
    const urlSessionId = params.get('resumeSessionId');

    if (urlSessionId) {
      // URL param takes precedence
      this.sessionId = urlSessionId;
    } else if (storedState.sessionId) {
      // Fall back to stored session
      this.sessionId = storedState.sessionId;
    }

    // Restore panel state (minimize/open)
    if (storedState.panelState === 'minimized' && this.modal) {
      this.toggleMinimize();
    }

    // If we have a sessionId, attempt to resume the conversation
    if (this.sessionId && this.api) {
      this.resumeSession();
    }
  }

  private async resumeSession(): Promise<void> {
    if (!this.sessionId || !this.api) return;

    try {
      const resumedSession = await this.api.resumeBotSession(this.sessionId);
      // V3 form will handle displaying the resumed conversation
      // This is stored for reference by the form
      (this as any).resumedSession = resumedSession;
    } catch (err) {
      // If resume fails, continue with fresh session
      console.warn('[FeedbackHub] Failed to resume session:', err);
      this.sessionId = null;
    }
  }

  // ─── Form modal ───────────────────────────────────────────────────────

  private renderFormModal(): void {
    this.modal = document.createElement('div');
    this.modal.className = `fh-modal ${this.position}`;
    this.modal.onclick = (e) => e.stopPropagation();

    // Header
    const header = document.createElement('div');
    header.className = 'fh-modal-header';
    header.innerHTML = `
      <h3>Send Feedback</h3>
      <div style="display:flex;gap:8px;align-items:center">
        ${this.feedbackFormVersion === 'v3' ? '<button class="fh-minimize-btn" title="Minimize" style="font-size:16px">−</button>' : ''}
        ${this.myFeedbackEnabled ? '<button class="fh-modal-close fh-my-feedback-btn" title="My Feedback" style="font-size:16px">📋</button>' : ''}
        <button class="fh-modal-close" title="Close">&times;</button>
      </div>
    `;
    header.querySelector('.fh-modal-close:last-child')!.addEventListener('click', () =>
      this.setView('closed'),
    );
    const myFbBtn = header.querySelector('.fh-my-feedback-btn');
    if (myFbBtn) {
      myFbBtn.addEventListener('click', () => this.loadAndShowMyFeedback());
    }
    // v3 minimize handler
    const minimizeBtn = header.querySelector('.fh-minimize-btn');
    if (minimizeBtn) {
      minimizeBtn.addEventListener('click', () => this.toggleMinimize());
    }
    this.modal.appendChild(header);

    // Body
    const body = document.createElement('div');
    body.className = 'fh-modal-body';

    // ── Capture buttons (v1/v2 only; v3 handles capture in chat) ─────
    if (this.feedbackFormVersion !== 'v3' && this.capturedBlob === null) {
      const captureRow = document.createElement('div');
      captureRow.className = 'fh-capture-row';

      if (this.screenshotEnabled) {
        const ssBtn = document.createElement('button');
        ssBtn.className = 'fh-capture-btn';
        ssBtn.innerHTML = '<span class="fh-capture-icon">📷</span>Screenshot';
        ssBtn.onclick = () => this.takeScreenshot();
        captureRow.appendChild(ssBtn);
      }

      if (this.videoEnabled) {
        const vidBtn = document.createElement('button');
        vidBtn.className = 'fh-capture-btn';
        vidBtn.innerHTML = '<span class="fh-capture-icon">🎥</span>Record';
        vidBtn.onclick = () => this.startRecording();
        captureRow.appendChild(vidBtn);
      }

      body.appendChild(captureRow);
    } else if (this.feedbackFormVersion !== 'v3') {
      // ── Capture preview ──────────────────────────────────────────
      const preview = document.createElement('div');
      preview.className = 'fh-preview';

      if (this.capturedType === 'screenshot') {
        const img = document.createElement('img');
        img.src = URL.createObjectURL(this.capturedBlob!);
        img.alt = 'Screenshot';
        preview.appendChild(img);
      } else {
        const video = document.createElement('video');
        video.src = URL.createObjectURL(this.capturedBlob!);
        video.controls = true;
        video.muted = true;
        preview.appendChild(video);
      }

      const removeBtn = document.createElement('button');
      removeBtn.className = 'fh-preview-remove';
      removeBtn.innerHTML = '✕';
      removeBtn.onclick = () => {
        this.capturedBlob = null;
        this.capturedType = 'none';
        this.setView('form');
      };
      preview.appendChild(removeBtn);
      body.appendChild(preview);
    }

    // ── Form fields ──────────────────────────────────────────────────
    const form = this.feedbackFormVersion === 'v3'
      ? buildV3BotModal({
          api: this.api,
          diagnostics: this.diagnostics,
          storedEmail: this.storedEmail,
          emailCaptureEnabled: this.emailCaptureEnabled,
          metadata: {
            url: window.location.href,
            pageTitle: document.title,
            userAgent: navigator.userAgent,
            viewport: { width: window.innerWidth, height: window.innerHeight },
            screenResolution: { width: screen.width, height: screen.height },
            timestamp: Date.now(),
            testerToken: this.testerToken,
            appName: this.config.appName,
            appVersion: this.config.appVersion,
            environment: this.config.environment,
            userId: this.config.userId,
            customMetadata: this.config.customMetadata,
            ...captureSystemInfo(),
          },
          resumedSession: (this as any).resumedSession,
          onSessionStarted: (sessionId: string) => {
            this.sessionId = sessionId;
            this.saveSessionState();
          },
          onSubmitted: () => {
            this.forceStandardForm = false;
            this.setView('success');
          },
          onSwitchToStandardForm: () => {
            this.forceStandardForm = true;
            this.setView('form');
          },
        }).form
      : this.feedbackFormVersion === 'v2'
      ? buildV2FormModal({
          capturedType: this.capturedType,
          storedEmail: this.storedEmail,
          emailCaptureEnabled: this.emailCaptureEnabled,
          availableCategories: this.enabledCategories,
          onSubmit: (form) => this.handleSubmit(form),
        }).form
      : this.buildV1Form();

    body.appendChild(form);
    this.modal.appendChild(body);
    this.root!.appendChild(this.modal);
  }

  // ─── V1 Form Builder ───────────────────────────────────────────────────

  private buildV1Form(): HTMLFormElement {
    const form = document.createElement('form');
    form.onsubmit = (e) => {
      e.preventDefault();
      this.handleSubmit(form);
    };

    // Category + Severity row
    const row1 = document.createElement('div');
    row1.className = 'fh-field-row';
    row1.innerHTML = `
      <div class="fh-field">
        <label>Category</label>
        <select name="category" required>
          ${buildCategoryOptionsHtml(this.enabledCategories, 'short')}
        </select>
      </div>
      <div class="fh-field">
        <label>Severity</label>
        <select name="severity" required>
          ${buildSeverityOptionsHtml()}
        </select>
      </div>
    `;
    form.appendChild(row1);

    // Title
    const titleField = document.createElement('div');
    titleField.className = 'fh-field';
    titleField.innerHTML = `
      <label>Title</label>
      <input type="text" name="title" placeholder="Brief summary of the issue" required maxlength="200" />
    `;
    form.appendChild(titleField);

    // Description
    const descField = document.createElement('div');
    descField.className = 'fh-field';
    descField.innerHTML = `
      <label>Description</label>
      <textarea name="description" placeholder="What happened? What did you expect?" rows="3"></textarea>
    `;
    form.appendChild(descField);

    // Optional email (for reply notifications)
    if (this.emailCaptureEnabled) {
      const emailField = document.createElement('div');
      emailField.className = 'fh-field';
      emailField.innerHTML = `
        <label>Email (optional — to receive replies &amp; track feedback)</label>
        <input type="email" name="testerEmail" placeholder="you@example.com" value="${this.storedEmail ? escapeHtml(this.storedEmail) : ''}" />
      `;
      form.appendChild(emailField);
    }

    // Submit
    const submitBtn = document.createElement('button');
    submitBtn.type = 'submit';
    submitBtn.className = 'fh-submit-btn';
    submitBtn.textContent = 'Submit Feedback';
    form.appendChild(submitBtn);

    return form;
  }

  // ─── Capture flows ────────────────────────────────────────────────────

  private async takeScreenshot(): Promise<void> {
    this.setView('closed'); // Hide widget during capture
    try {
      // Small delay so the widget is fully hidden
      await new Promise((r) => setTimeout(r, 150));
      const canvas = await captureScreenshot();
      const overlay = new AnnotationOverlay(canvas);
      const blob = await overlay.open();
      this.capturedBlob = blob;
      this.capturedType = 'screenshot';
      this.setView('form');
    } catch (err) {
      // User cancelled annotation — re-open form
      if ((err as Error).message !== 'Annotation cancelled') {
        console.warn('[FeedbackHub] Screenshot error:', err);
      }
      this.setView('form');
    }
  }

  private startRecording(): void {
    this.setView('closed'); // Hide modal during recording
    this.viewState = 'recording';
    this.recorder.start();
  }

  // ─── Recording bar ────────────────────────────────────────────────────

  private showRecordingBar(): void {
    this.recordingBar = document.createElement('div');
    this.recordingBar.className = 'fh-recording-bar';
    this.recordingBar.innerHTML = `
      <span class="fh-recording-dot"></span>
      <span>Recording — <span class="fh-rec-time">0:00</span></span>
      <button class="fh-recording-stop">⏹ Stop Recording</button>
    `;
    this.recordingBar.querySelector('.fh-recording-stop')!.addEventListener('click', () => {
      this.recorder.stop();
    });
    this.root!.appendChild(this.recordingBar);
  }

  private updateRecordingTime(seconds: number): void {
    const el = this.recordingBar?.querySelector('.fh-rec-time');
    if (el) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      el.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  }

  private hideRecordingBar(): void {
    this.recordingBar?.remove();
    this.recordingBar = null;
  }

  // ─── Form submission ──────────────────────────────────────────────────

  private async handleSubmit(form: HTMLFormElement): Promise<void> {
    const data = new FormData(form);
    const selectedCategory = data.get('category') as string;
    const fallbackCategory = this.enabledCategories[0] ?? 'bug';

    const feedbackData: PendingFeedbackData = {
      category: isFeedbackCategory(selectedCategory) ? selectedCategory : fallbackCategory,
      title: data.get('title') as string,
      description: (data.get('description') as string) || '',
      expectedBehavior: ((data.get('expectedBehavior') as string) || '').trim() || undefined,
      stepsToReproduce: ((data.get('stepsToReproduce') as string) || '').trim() || undefined,
      successCriteria: ((data.get('successCriteria') as string) || '').trim() || undefined,
      contextualInfo: ((data.get('contextualInfo') as string) || '').trim() || undefined,
    };

    // Check feedback quality (soft gate)
    const qualityCheck = checkFeedbackQuality(feedbackData);
    if (qualityCheck.suggestions.length > 0) {
      // Render quality gate inline within the widget modal body
      const modalBody = this.modal?.querySelector('.fh-modal-body') as HTMLElement | null;
      if (modalBody) {
        renderInlineQualityGate({
          container: modalBody,
          suggestions: qualityCheck.suggestions,
          onImproveNow: () => {
            // Form is restored by renderInlineQualityGate
          },
          onSubmitAnyway: async () => {
            try {
              const payload = this.buildFeedbackPayload(feedbackData, data, {
                qualityGateShown: true,
                qualityGateSuggestions: qualityCheck.suggestions,
                qualityGateDismissed: true,
              });
              await this.api.submitFeedback(
                payload,
                this.capturedBlob ?? undefined,
                this.capturedType !== 'none' ? this.capturedType : undefined,
              );
              const submittedEmail = payload.metadata?.testerEmail;
              if (submittedEmail) {
                this.storedEmail = submittedEmail;
                localStorage.setItem(FeedbackUI.EMAIL_STORAGE_KEY, submittedEmail);
              }
              this.setView('success');
            } catch (err) {
              console.error('[FeedbackHub] Submission failed:', err);
              const inlineEl = modalBody.querySelector('.fh-quality-gate-inline');
              if (inlineEl) {
                inlineEl.innerHTML = `
                  <div style="text-align:center;padding:24px 0;">
                    <div style="font-size:24px;margin-bottom:12px;">❌</div>
                    <p style="margin:0 0 12px;font-size:14px;color:#ef4444;font-weight:500;">Failed to submit. Please try again.</p>
                    <button class="fh-quality-gate-retry" style="padding:8px 16px;border:none;border-radius:6px;background:#3b82f6;color:white;font-size:13px;font-weight:500;cursor:pointer;">Try Again</button>
                  </div>
                `;
                inlineEl.querySelector('.fh-quality-gate-retry')?.addEventListener('click', () => {
                  this.setView('form');
                });
              }
            }
          },
        });
        return;
      }

      // Fallback: Show quality gate as page-level modal (if modal body not found)
      renderQualityGateModal({
        category: feedbackData.category,
        suggestions: qualityCheck.suggestions,
        onImproveNow: () => {
          dismissQualityGateModal();
        },
        onSubmitAnyway: () => {
          dismissQualityGateModal();
          this.submitFeedbackPayload(form, feedbackData, data, {
            qualityGateShown: true,
            qualityGateSuggestions: qualityCheck.suggestions,
            qualityGateDismissed: true,
          });
        },
      });
      return;
    }

    // Quality check passed or no suggestions — proceed with submission
    this.submitFeedbackPayload(form, feedbackData, data, {
      qualityGateShown: false,
      qualityGateSuggestions: [],
      qualityGateDismissed: false,
    });
  }

  private async submitFeedbackPayload(
    form: HTMLFormElement,
    feedbackData: PendingFeedbackData,
    formData: FormData,
    qualityGateMeta: {
      qualityGateShown: boolean;
      qualityGateSuggestions: string[];
      qualityGateDismissed: boolean;
    },
  ): Promise<void> {
    const submitBtn = form.querySelector('.fh-submit-btn') as HTMLButtonElement;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Submitting...';

    const payload = this.buildFeedbackPayload(feedbackData, formData, qualityGateMeta);

    try {
      await this.api.submitFeedback(
        payload,
        this.capturedBlob ?? undefined,
        this.capturedType !== 'none' ? this.capturedType : undefined,
      );

      // Save email to localStorage if provided
      const submittedEmail = payload.metadata?.testerEmail;
      if (submittedEmail) {
        this.storedEmail = submittedEmail;
        localStorage.setItem(FeedbackUI.EMAIL_STORAGE_KEY, submittedEmail);
      }

      this.setView('success');
    } catch (err) {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Submit Feedback';
      console.error('[FeedbackHub] Submission failed:', err);
      // Show inline error
      let errEl = form.querySelector('.fh-error') as HTMLDivElement;
      if (!errEl) {
        errEl = document.createElement('div');
        errEl.className = 'fh-error';
        errEl.style.cssText = 'color:#ef4444;font-size:13px;margin-top:8px;text-align:center';
        form.appendChild(errEl);
      }
      errEl.textContent = 'Failed to submit. Please try again.';
    }
  }

  private buildFeedbackPayload(
    feedbackData: PendingFeedbackData,
    formData: FormData,
    qualityGateMeta: {
      qualityGateShown: boolean;
      qualityGateSuggestions: string[];
      qualityGateDismissed: boolean;
    },
  ): FeedbackPayload {
    return {
      category: feedbackData.category,
      title: feedbackData.title,
      description: feedbackData.description,
      expectedBehavior: feedbackData.expectedBehavior,
      stepsToReproduce: feedbackData.stepsToReproduce,
      successCriteria: feedbackData.successCriteria,
      contextualInfo: feedbackData.contextualInfo,
      formVersion: this.feedbackFormVersion,
      qualityGateShown: qualityGateMeta.qualityGateShown,
      qualityGateSuggestions: qualityGateMeta.qualityGateSuggestions,
      qualityGateDismissed: qualityGateMeta.qualityGateDismissed,
      severity: formData.get('severity') as FeedbackSeverity,
      captureType: this.capturedType,
      metadata: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        viewport: { width: window.innerWidth, height: window.innerHeight },
        screenResolution: { width: screen.width, height: screen.height },
        connection: (navigator as any).connection ? {
          effectiveType: (navigator as any).connection.effectiveType,
          downlink: (navigator as any).connection.downlink,
          rtt: (navigator as any).connection.rtt,
        } : undefined,
        timestamp: Date.now(),
        appName: this.config.appName,
        appVersion: this.config.appVersion,
        environment: this.config.environment,
        userId: this.config.userId,
        customMetadata: this.config.customMetadata,
        testerToken: this.testerToken,
        testerEmail: (formData.get('testerEmail') as string) || this.storedEmail || undefined,
        ...captureSystemInfo(),
      },
      diagnostics: this.diagnostics.snapshot(),
    };
  }

  // ─── Success modal ────────────────────────────────────────────────────

  private renderSuccessModal(): void {
    this.modal = document.createElement('div');
    this.modal.className = `fh-modal ${this.position}`;
    this.modal.onclick = (e) => e.stopPropagation();

    const header = document.createElement('div');
    header.className = 'fh-modal-header';
    header.innerHTML = `<h3>Feedback Sent</h3><button class="fh-modal-close">&times;</button>`;
    header.querySelector('button')!.onclick = () => this.setView('closed');
    this.modal.appendChild(header);

    const body = document.createElement('div');
    body.className = 'fh-modal-body';
    body.innerHTML = `
      <div class="fh-success">
        <div class="fh-success-icon">✅</div>
        <h4>Thank you!</h4>
        <p>Your feedback has been received. ${this.myFeedbackEnabled ? 'You can track it from the 📋 button.' : ''}</p>
      </div>
    `;
    this.modal.appendChild(body);
    this.root!.appendChild(this.modal);

    // Auto-close after 3 seconds
    setTimeout(() => {
      if (this.viewState === 'success') this.setView('closed');
    }, 3000);
  }

  // ─── Email prompt ──────────────────────────────────────────────────────

  private renderEmailPromptModal(): void {
    this.modal = document.createElement('div');
    this.modal.className = `fh-modal ${this.position}`;
    this.modal.onclick = (e) => e.stopPropagation();

    const header = document.createElement('div');
    header.className = 'fh-modal-header';
    header.innerHTML = `
      <div style="display:flex;align-items:center;gap:8px">
        <button class="fh-modal-close fh-back-btn" title="Back" style="font-size:18px">←</button>
        <h3>My Feedback</h3>
      </div>
      <button class="fh-modal-close" title="Close">&times;</button>
    `;
    header.querySelector('.fh-back-btn')!.addEventListener('click', () =>
      this.setView('form'),
    );
    header.querySelector('.fh-modal-close:last-child')!.addEventListener('click', () =>
      this.setView('closed'),
    );
    this.modal.appendChild(header);

    const body = document.createElement('div');
    body.className = 'fh-modal-body';
    body.innerHTML = `
      <div style="text-align:center;padding:16px 0">
        <p style="font-size:14px;color:var(--fh-text-secondary);margin-bottom:16px">Enter your email to view feedback you've submitted.</p>
        <form class="fh-email-prompt-form">
          <div class="fh-field">
            <input type="email" name="email" placeholder="you@example.com" required style="width:100%" />
          </div>
          <button type="submit" class="fh-submit-btn" style="margin-top:8px;width:100%">View My Feedback</button>
        </form>
      </div>
    `;

    const form = body.querySelector('.fh-email-prompt-form') as HTMLFormElement;
    form.onsubmit = (e) => {
      e.preventDefault();
      const email = (new FormData(form).get('email') as string).trim();
      if (!email) return;
      this.storedEmail = email;
      localStorage.setItem(FeedbackUI.EMAIL_STORAGE_KEY, email);
      this.loadAndShowMyFeedback();
    };

    this.modal.appendChild(body);
    this.root!.appendChild(this.modal);
  }

  // ─── My Feedback panel ────────────────────────────────────────────────

  private async loadAndShowMyFeedback(): Promise<void> {
    if (!this.storedEmail) {
      this.setView('email-prompt');
      return;
    }
    try {
      const response = await this.api.getMyFeedback(this.storedEmail);
      this.myFeedbackList = response.items;
      this.myFeedbackApps = response.apps;
      this.loadFiltersFromStorage();
    } catch {
      this.myFeedbackList = [];
      this.myFeedbackApps = [];
    }
    this.setView('my-feedback');
    this.startFeedbackPolling();
  }

  private loadFiltersFromStorage(): void {
    try {
      const stored = localStorage.getItem(FeedbackUI.FEEDBACK_FILTERS_STORAGE_KEY);
      if (stored) {
        const filters = JSON.parse(stored);
        this.selectedAppIds = new Set(filters.appIds || []);
        this.selectedStatuses = new Set(filters.statuses || []);
      }
    } catch {
      // Ignore storage errors
    }
  }

  private saveFiltersToStorage(): void {
    try {
      const filters = {
        appIds: Array.from(this.selectedAppIds),
        statuses: Array.from(this.selectedStatuses),
      };
      localStorage.setItem(FeedbackUI.FEEDBACK_FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch {
      // Ignore storage errors
    }
  }

  private getFilteredFeedbackList(): FeedbackSummary[] {
    return this.myFeedbackList.filter((item) => {
      const appMatches = this.selectedAppIds.size === 0 || this.selectedAppIds.has(item.appId);
      const statusMatches = this.selectedStatuses.size === 0 || this.selectedStatuses.has(item.status);
      return appMatches && statusMatches;
    });
  }

  private renderMyFeedbackModal(): void {
    this.modal = document.createElement('div');
    this.modal.className = `fh-modal ${this.position}`;
    this.modal.onclick = (e) => e.stopPropagation();

    const header = document.createElement('div');
    header.className = 'fh-modal-header';
    header.innerHTML = `
      <div style="display:flex;align-items:center;gap:8px">
        <button class="fh-modal-close" title="Back" style="font-size:18px">←</button>
        <h3>My Feedback</h3>
      </div>
      <button class="fh-modal-close" title="Close">&times;</button>
    `;
    header.querySelector('.fh-modal-close')!.addEventListener('click', () =>
      this.setView('form'),
    );
    header.querySelector('.fh-modal-close:last-child')!.addEventListener('click', () =>
      this.setView('closed'),
    );
    this.modal.appendChild(header);

    const body = document.createElement('div');
    body.className = 'fh-modal-body';

    // Add filter section
    if (this.myFeedbackList.length > 0) {
      const filterContainer = document.createElement('div');
      filterContainer.style.cssText =
        'margin-bottom:12px;padding:12px;border:1px solid var(--fh-border);border-radius:8px;background:var(--fh-background-secondary)';

      const filterLabel = document.createElement('div');
      filterLabel.style.cssText = 'font-size:11px;font-weight:600;color:var(--fh-text-secondary);margin-bottom:8px';
      filterLabel.textContent = 'FILTERS';
      filterContainer.appendChild(filterLabel);

      // App filter
      if (this.myFeedbackApps.length > 0) {
        const appFilterDiv = document.createElement('div');
        appFilterDiv.style.cssText = 'margin-bottom:8px';
        const appLabel = document.createElement('div');
        appLabel.style.cssText = 'font-size:10px;color:var(--fh-text-secondary);margin-bottom:4px';
        appLabel.textContent = 'Apps';
        appFilterDiv.appendChild(appLabel);

        const appChips = document.createElement('div');
        appChips.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px';
        for (const app of this.myFeedbackApps) {
          const chip = document.createElement('button');
          chip.style.cssText =
            `padding:4px 8px;border:1px solid var(--fh-border);border-radius:4px;background:${this.selectedAppIds.has(app.id) ? 'var(--fh-primary)' : 'var(--fh-background)'};color:${this.selectedAppIds.has(app.id) ? 'white' : 'var(--fh-text)'};font-size:11px;cursor:pointer;transition:all 0.2s;`;
          chip.textContent = app.name;
          chip.onclick = () => {
            if (this.selectedAppIds.has(app.id)) {
              this.selectedAppIds.delete(app.id);
            } else {
              this.selectedAppIds.add(app.id);
            }
            this.saveFiltersToStorage();
            this.clearModal();
            this.renderMyFeedbackModal();
          };
          appChips.appendChild(chip);
        }
        appFilterDiv.appendChild(appChips);
        filterContainer.appendChild(appFilterDiv);
      }

      // Status filter
      const statusFilterDiv = document.createElement('div');
      const statuses: FeedbackStatus[] = ['new', 'in-progress', 'resolved', 'closed'];
      const statusLabel = document.createElement('div');
      statusLabel.style.cssText = 'font-size:10px;color:var(--fh-text-secondary);margin-bottom:4px';
      statusLabel.textContent = 'Status';
      statusFilterDiv.appendChild(statusLabel);

      const statusChips = document.createElement('div');
      statusChips.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px';
      for (const status of statuses) {
        const chip = document.createElement('button');
        const isSelected = this.selectedStatuses.has(status);
        chip.style.cssText =
          `padding:4px 8px;border:1px solid var(--fh-border);border-radius:4px;background:${isSelected ? 'var(--fh-primary)' : 'var(--fh-background)'};color:${isSelected ? 'white' : 'var(--fh-text)'};font-size:11px;cursor:pointer;transition:all 0.2s;`;
        chip.textContent = getFeedbackStatusLabel(status);
        chip.onclick = () => {
          if (this.selectedStatuses.has(status)) {
            this.selectedStatuses.delete(status);
          } else {
            this.selectedStatuses.add(status);
          }
          this.saveFiltersToStorage();
          this.clearModal();
          this.renderMyFeedbackModal();
        };
        statusChips.appendChild(chip);
      }
      statusFilterDiv.appendChild(statusChips);
      filterContainer.appendChild(statusFilterDiv);

      body.appendChild(filterContainer);
    }

    const filteredList = this.getFilteredFeedbackList();
    if (this.myFeedbackList.length === 0) {
      body.innerHTML += `<p style="text-align:center;color:var(--fh-text-secondary)">No feedback submitted yet.</p>`;
    } else if (filteredList.length === 0) {
      body.innerHTML += `<p style="text-align:center;color:var(--fh-text-secondary)">No feedback matches the selected filters.</p>`;
    } else {
      for (const item of filteredList) {
        const card = document.createElement('div');
        card.style.cssText =
          'padding:12px;border:1px solid var(--fh-border);border-radius:8px;margin-bottom:10px;cursor:pointer;transition:border-color 0.2s';
        card.onmouseenter = () => (card.style.borderColor = 'var(--fh-primary)');
        card.onmouseleave = () => (card.style.borderColor = 'var(--fh-border)');

        card.innerHTML = `
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px">
            <span style="font-weight:600;font-size:13px">${FEEDBACK_CATEGORY_ICON_BY_VALUE[item.category] ?? ''} ${escapeHtml(item.title)}</span>
            <span style="font-size:11px;padding:2px 8px;border-radius:10px;background:${getFeedbackStatusColor(item.status)};color:white">${escapeHtml(getFeedbackStatusLabel(item.status))}</span>
          </div>
          <div style="font-size:12px;color:var(--fh-text-secondary)">
            ${new Date(item.createdAt).toLocaleDateString()} · ${item.severity}
            ${item.unreadComments > 0 ? `<span style="color:var(--fh-primary);font-weight:600"> · ${item.unreadComments} new reply${item.unreadComments > 1 ? 's' : ''}</span>` : ''}
            ${item.verificationStatus === 'awaiting-verification' ? `<span style="color:#b45309;font-weight:600"> · Verify fix</span>` : ''}
          </div>
        `;

        card.onclick = () => this.openThread(item.id);
        body.appendChild(card);
      }
    }

    this.modal.appendChild(body);
    this.root!.appendChild(this.modal);
  }

  // ─── Thread view (communication loop) ─────────────────────────────────

  private async openThread(feedbackId: string): Promise<void> {
    this.stopFeedbackPolling(); // Stop polling while viewing thread
    this.activeThreadId = feedbackId;
    try {
      this.activeThreadComments = await this.api.getComments(feedbackId, this.testerToken, this.storedEmail || undefined);
    } catch {
      this.activeThreadComments = [];
    }
    this.setView('thread');
  }

  private renderThreadModal(): void {
    const item = this.myFeedbackList.find((f) => f.id === this.activeThreadId);
    if (!item) return;

    this.modal = document.createElement('div');
    this.modal.className = `fh-modal ${this.position}`;
    this.modal.onclick = (e) => e.stopPropagation();

    // Header
    const header = document.createElement('div');
    header.className = 'fh-modal-header';
    header.innerHTML = `
      <div style="display:flex;align-items:center;gap:8px">
        <button class="fh-modal-close fh-back-btn" title="Back" style="font-size:18px">←</button>
        <h3 style="font-size:14px;max-width:250px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escapeHtml(item.title)}</h3>
      </div>
      <button class="fh-modal-close" title="Close">&times;</button>
    `;
    header.querySelector('.fh-back-btn')!.addEventListener('click', () =>
      this.loadAndShowMyFeedback(),
    );
    header.querySelector('.fh-modal-close:last-child')!.addEventListener('click', () =>
      this.setView('closed'),
    );
    this.modal.appendChild(header);

    // Body — comment thread
    const body = document.createElement('div');
    body.className = 'fh-modal-body';
    body.style.cssText = 'display:flex;flex-direction:column;padding-bottom:0';

    // Status bar
    const statusBar = document.createElement('div');
    statusBar.style.cssText =
      'font-size:12px;color:var(--fh-text-secondary);margin-bottom:12px;padding-bottom:12px;border-bottom:1px solid var(--fh-border)';
    statusBar.textContent = `Status: ${item.status} · ${item.severity} · ${item.category}`;
    body.appendChild(statusBar);

    if (item.verificationChecklist && item.verificationChecklist.length > 0) {
      const checklistBox = document.createElement('div');
      checklistBox.style.cssText = 'margin-bottom:12px;padding:10px 12px;border:1px solid #fde68a;border-radius:10px;background:#fffbeb';
      const pendingCount = item.verificationChecklist.filter((entry) => entry.status === 'pending').length;
      const heading = document.createElement('div');
      heading.style.cssText = 'font-size:12px;font-weight:600;color:#92400e;margin-bottom:6px';
      heading.textContent = `Verification checklist${pendingCount > 0 ? ` · ${pendingCount} pending` : ''}`;
      checklistBox.appendChild(heading);

      const list = document.createElement('div');
      list.style.cssText = 'display:flex;flex-direction:column;gap:8px';
      checklistBox.appendChild(list);

      for (const entry of item.verificationChecklist) {
        const row = document.createElement('div');
        row.style.cssText = 'font-size:12px;color:#78350f;border:1px solid #fcd34d;border-radius:8px;padding:8px;background:#fff7ed';

        const title = document.createElement('div');
        title.style.cssText = 'display:flex;align-items:flex-start;gap:6px;margin-bottom:6px';
        title.innerHTML = `<span style="font-size:13px">${entry.status === 'verified' ? '✓' : entry.status === 'failed' ? '✕' : '•'}</span><span>${escapeHtml(entry.step)}${entry.context ? `<span style="display:block;color:#a16207;font-size:11px">${escapeHtml(entry.context)}</span>` : ''}</span>`;
        row.appendChild(title);

        const controls = document.createElement('div');
        controls.style.cssText = 'display:flex;flex-direction:column;gap:6px';

        const statusSelect = document.createElement('select');
        statusSelect.style.cssText = 'padding:6px;border:1px solid #f59e0b;border-radius:6px;background:white;font-size:12px';
        statusSelect.innerHTML = `
          <option value="pending">Pending</option>
          <option value="verified">Verified</option>
          <option value="failed">Failed</option>
        `;
        statusSelect.value = entry.status;

        const noteInput = document.createElement('input');
        noteInput.type = 'text';
        noteInput.placeholder = 'Optional note (what happened?)';
        noteInput.value = entry.note ?? '';
        noteInput.style.cssText = 'padding:6px;border:1px solid #f59e0b;border-radius:6px;background:white;font-size:12px';

        const evidenceInput = document.createElement('input');
        evidenceInput.type = 'text';
        evidenceInput.placeholder = 'Evidence URL (optional)';
        evidenceInput.value = entry.evidenceUrl ?? '';
        evidenceInput.style.cssText = 'padding:6px;border:1px solid #f59e0b;border-radius:6px;background:white;font-size:12px';

        const evidenceFileInput = document.createElement('input');
        evidenceFileInput.type = 'file';
        evidenceFileInput.accept = 'image/*,video/*';
        evidenceFileInput.style.display = 'none';

        let pendingEvidenceBlob: Blob | null = null;
        const evidenceButtons = document.createElement('div');
        evidenceButtons.style.cssText = 'display:flex;gap:6px;align-items:center';
        const attachEvidenceBtn = document.createElement('button');
        attachEvidenceBtn.type = 'button';
        attachEvidenceBtn.textContent = 'Attach evidence';
        attachEvidenceBtn.style.cssText = 'padding:6px 8px;border:1px solid #f59e0b;border-radius:6px;background:white;color:#92400e;cursor:pointer;font-size:11px';
        attachEvidenceBtn.onclick = () => evidenceFileInput.click();
        const evidenceLabel = document.createElement('span');
        evidenceLabel.style.cssText = 'font-size:11px;color:#a16207';
        evidenceButtons.appendChild(attachEvidenceBtn);
        evidenceButtons.appendChild(evidenceLabel);
        evidenceButtons.appendChild(evidenceFileInput);

        evidenceFileInput.onchange = () => {
          const file = evidenceFileInput.files?.[0];
          pendingEvidenceBlob = file ?? null;
          evidenceLabel.textContent = file ? file.name : '';
          evidenceFileInput.value = '';
        };

        const saveBtn = document.createElement('button');
        saveBtn.type = 'button';
        saveBtn.textContent = 'Save verification update';
        saveBtn.style.cssText = 'padding:7px 10px;border:none;border-radius:6px;background:#b45309;color:white;cursor:pointer;font-size:12px';
        saveBtn.onclick = async () => {
          if (!this.activeThreadId) return;
          saveBtn.disabled = true;
          saveBtn.textContent = 'Saving…';
          try {
            let evidenceUrl = evidenceInput.value.trim() || undefined;
            if (pendingEvidenceBlob) {
              const evidenceComment = await this.api.postComment(
                this.activeThreadId,
                this.testerToken,
                `Verification evidence for: ${entry.step}`,
                this.storedEmail || undefined,
                pendingEvidenceBlob,
              );
              evidenceUrl = evidenceComment.attachments?.[0]?.url || evidenceUrl;
            }

            const updated = await this.api.updateVerificationItems(
              this.activeThreadId,
              [{
                id: entry.id,
                status: statusSelect.value as 'pending' | 'verified' | 'failed',
                note: noteInput.value.trim() || undefined,
                evidenceUrl,
              }],
              this.testerToken,
              this.storedEmail || undefined,
            );

            const index = this.myFeedbackList.findIndex((listEntry) => listEntry.id === this.activeThreadId);
            if (index >= 0) {
              this.myFeedbackList[index] = {
                ...this.myFeedbackList[index],
                verificationChecklist: updated.verificationChecklist as any,
                verificationStatus: updated.verificationStatus as any,
              };
            }

            this.activeThreadComments = await this.api.getComments(
              this.activeThreadId,
              this.testerToken,
              this.storedEmail || undefined,
            );

            this.setView('thread');
          } catch {
            saveBtn.disabled = false;
            saveBtn.textContent = 'Save verification update';
          }
        };

        controls.appendChild(statusSelect);
        controls.appendChild(noteInput);
        controls.appendChild(evidenceInput);
        controls.appendChild(evidenceButtons);
        controls.appendChild(saveBtn);
        row.appendChild(controls);

        list.appendChild(row);
      }

      const confirmBtn = document.createElement('button');
      confirmBtn.textContent = 'Mark all pending as verified';
      confirmBtn.style.cssText = 'margin-top:8px;padding:8px 10px;border:none;border-radius:6px;background:#b45309;color:white;cursor:pointer;font-size:12px';
      confirmBtn.onclick = async () => {
        const pendingIds = item.verificationChecklist?.filter((entry) => entry.status === 'pending').map((entry) => entry.id) ?? [];
        if (!pendingIds.length || !this.activeThreadId) return;
        confirmBtn.disabled = true;
        confirmBtn.textContent = 'Saving…';
        try {
          const updated = await this.api.confirmVerification(this.activeThreadId, pendingIds, this.testerToken, this.storedEmail || undefined);
          const index = this.myFeedbackList.findIndex((entry) => entry.id === this.activeThreadId);
          if (index >= 0) {
            this.myFeedbackList[index] = {
              ...this.myFeedbackList[index],
              verificationChecklist: updated.verificationChecklist as any,
              verificationStatus: updated.verificationStatus as any,
            };
          }
          this.setView('thread');
        } catch {
          confirmBtn.disabled = false;
          confirmBtn.textContent = 'Confirm pending checks';
        }
      };
      checklistBox.appendChild(confirmBtn);
      body.appendChild(checklistBox);
    }

    // Messages
    const messages = document.createElement('div');
    messages.style.cssText = 'flex:1;overflow-y:auto;margin-bottom:12px';

    if (this.activeThreadComments.length === 0) {
      messages.innerHTML = `<p style="text-align:center;color:var(--fh-text-secondary);font-size:13px">No replies yet.</p>`;
    } else {
      for (const comment of this.activeThreadComments) {
        const isTeam = comment.author === 'team';
        const msg = document.createElement('div');
        msg.style.cssText = `margin-bottom:10px;display:flex;${isTeam ? '' : 'justify-content:flex-end'}`;

        let attachmentHtml = '';
        if (comment.attachments?.length) {
          for (const att of comment.attachments) {
            if (att.type === 'video') {
              attachmentHtml += `<video controls playsinline style="max-width:100%;border-radius:8px;margin-top:6px" src="${escapeHtml(att.url)}"></video>`;
            } else {
              attachmentHtml += `<img style="max-width:100%;border-radius:8px;margin-top:6px;cursor:pointer" src="${escapeHtml(att.url)}" onclick="window.open(this.src,'_blank')"/>`;
            }
          }
        }

        msg.innerHTML = `
          <div style="max-width:85%;padding:10px 14px;border-radius:12px;font-size:13px;
            ${isTeam ? 'background:var(--fh-bg-secondary);border:1px solid var(--fh-border)' : 'background:var(--fh-primary);color:white'}">
            ${isTeam ? `<div style="font-size:11px;font-weight:600;margin-bottom:4px;color:var(--fh-text-secondary)">${escapeHtml(comment.authorName ?? 'Team')}</div>` : ''}
            ${comment.message ? `<div>${escapeHtml(comment.message)}</div>` : ''}
            ${attachmentHtml}
            <div style="font-size:10px;margin-top:4px;opacity:0.7">${new Date(comment.createdAt).toLocaleString()}</div>
          </div>
        `;
        messages.appendChild(msg);
      }
    }
    body.appendChild(messages);

    // Attachment state
    let pendingMedia: Blob | null = null;

    // Hidden file input for choosing images/videos
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*,video/*';
    fileInput.style.display = 'none';
    body.appendChild(fileInput);

    // Preview area (shown when a file is staged)
    const previewRow = document.createElement('div');
    previewRow.style.cssText = 'display:none;align-items:center;gap:8px;padding:8px 0';
    body.appendChild(previewRow);

    const showPreview = (blob: Blob) => {
      pendingMedia = blob;
      const url = URL.createObjectURL(blob);
      previewRow.innerHTML = '';
      if (blob.type.startsWith('video/')) {
        const vid = document.createElement('video');
        vid.src = url;
        vid.style.cssText = 'height:48px;border-radius:4px';
        previewRow.appendChild(vid);
      } else {
        const img = document.createElement('img');
        img.src = url;
        img.style.cssText = 'height:48px;border-radius:4px';
        previewRow.appendChild(img);
      }
      const removeBtn = document.createElement('button');
      removeBtn.textContent = '✕';
      removeBtn.style.cssText = 'background:none;border:none;cursor:pointer;font-size:14px;color:var(--fh-text-secondary)';
      removeBtn.onclick = () => {
        pendingMedia = null;
        previewRow.style.display = 'none';
        URL.revokeObjectURL(url);
      };
      previewRow.appendChild(removeBtn);
      previewRow.style.display = 'flex';
    };

    fileInput.onchange = () => {
      const file = fileInput.files?.[0];
      if (file) showPreview(file);
      fileInput.value = '';
    };

    // Reply input row
    const replyRow = document.createElement('div');
    replyRow.style.cssText =
      'display:flex;gap:8px;padding:12px 0;border-top:1px solid var(--fh-border);align-items:center';

    // Attach button
    const attachBtn = document.createElement('button');
    attachBtn.title = 'Attach image or video';
    attachBtn.innerHTML = '📎';
    attachBtn.style.cssText =
      'background:none;border:none;cursor:pointer;font-size:18px;padding:4px;flex-shrink:0';
    attachBtn.onclick = () => fileInput.click();

    // Screenshot button
    const screenshotBtn = document.createElement('button');
    screenshotBtn.title = 'Take screenshot';
    screenshotBtn.innerHTML = '📷';
    screenshotBtn.style.cssText =
      'background:none;border:none;cursor:pointer;font-size:18px;padding:4px;flex-shrink:0';
    screenshotBtn.onclick = async () => {
      try {
        // Hide the widget UI temporarily
        if (this.root) this.root.style.display = 'none';
        const canvas = await captureScreenshot();
        if (this.root) this.root.style.display = '';
        if (canvas) {
          const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
              (value) => {
                if (value) {
                  resolve(value);
                  return;
                }

                reject(new Error('Failed to create screenshot blob'));
              },
              'image/png',
              0.92,
            );
          });
          showPreview(blob);
        }
      } catch {
        if (this.root) this.root.style.display = '';
      }
    };

    const replyInput = document.createElement('input');
    replyInput.type = 'text';
    replyInput.placeholder = 'Type a reply...';
    replyInput.style.cssText =
      'flex:1;padding:8px 12px;border:1px solid var(--fh-border);border-radius:6px;font-size:14px;font-family:inherit;min-width:0';

    const sendBtn = document.createElement('button');
    sendBtn.textContent = 'Send';
    sendBtn.style.cssText =
      'padding:8px 16px;border:none;border-radius:6px;background:var(--fh-primary);color:white;cursor:pointer;font-size:14px;flex-shrink:0';

    const sendReply = async () => {
      const msg = replyInput.value.trim();
      if (!msg && !pendingMedia) return;
      if (!this.activeThreadId) return;
      sendBtn.disabled = true;
      try {
        await this.api.postComment(
          this.activeThreadId,
          this.testerToken,
          msg,
          this.storedEmail || undefined,
          pendingMedia || undefined,
        );
        pendingMedia = null;
        // Reload thread
        this.activeThreadComments = await this.api.getComments(
          this.activeThreadId,
          this.testerToken,
          this.storedEmail || undefined,
        );
        this.setView('thread');
      } catch {
        sendBtn.disabled = false;
      }
    };

    sendBtn.onclick = sendReply;
    replyInput.onkeydown = (e) => {
      if (e.key === 'Enter') sendReply();
    };

    replyRow.appendChild(attachBtn);
    replyRow.appendChild(screenshotBtn);
    replyRow.appendChild(replyInput);
    replyRow.appendChild(sendBtn);
    body.appendChild(replyRow);

    this.modal.appendChild(body);
    this.root!.appendChild(this.modal);

    // Scroll to bottom of messages
    messages.scrollTop = messages.scrollHeight;
  }

  // ─── Feedback Polling & Notifications ─────────────────────────────────

  private startFeedbackPolling(): void {
    if (!this.myFeedbackEnabled || !this.storedEmail) return;

    // Stop any existing polling
    this.stopFeedbackPolling();

    // Initial poll
    this.pollForNewReplies();

    // Start polling every 60 seconds
    this.feedbackPollingInterval = setInterval(() => {
      this.pollForNewReplies();
    }, 60000);

    // Pause polling when tab is hidden, resume when visible
    if (!this.feedbackPollingPausedByVisibility) {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopFeedbackPolling();
          this.feedbackPollingPausedByVisibility = true;
        } else {
          this.feedbackPollingPausedByVisibility = false;
          this.startFeedbackPolling();
        }
      });
    }
  }

  private stopFeedbackPolling(): void {
    if (this.feedbackPollingInterval) {
      clearInterval(this.feedbackPollingInterval);
      this.feedbackPollingInterval = null;
    }
  }

  private async pollForNewReplies(): Promise<void> {
    if (!this.storedEmail || this.viewState === 'closed') {
      this.stopFeedbackPolling();
      return;
    }

    try {
      const response = await this.api.getMyFeedback(this.storedEmail);
      const newItems = response.items;

      // Check for new replies
      for (const newItem of newItems) {
        const oldItem = this.myFeedbackList.find((f) => f.id === newItem.id);
        if (oldItem && newItem.unreadComments > oldItem.unreadComments) {
          // New reply detected!
          this.showReplyNotification(newItem);
        }
      }

      // Update list with latest data
      this.myFeedbackList = newItems;
      this.myFeedbackApps = response.apps;

      // Re-render if my-feedback view is currently active
      if (this.viewState === 'my-feedback') {
        this.clearModal();
        this.renderMyFeedbackModal();
      } else if (this.viewState === 'form' || this.viewState === 'success' || this.viewState === 'recording') {
        // Show badge on the trigger button if there are unread comments
        this.updateMyFeedbackBadge();
      }
    } catch {
      // Silently handle polling errors
    }
  }

  private updateMyFeedbackBadge(): void {
    const hasUnread = this.myFeedbackList.some((item) => item.unreadComments > 0);
    if (this.triggerBtn) {
      const badge = this.triggerBtn.querySelector('.fh-badge');
      if (hasUnread) {
        if (!badge) {
          const newBadge = document.createElement('span');
          newBadge.className = 'fh-badge';
          newBadge.style.cssText =
            'position:absolute;top:-4px;right:-4px;width:10px;height:10px;background:var(--fh-error,#ef4444);border-radius:50%;border:2px solid var(--fh-background)';
          this.triggerBtn.style.position = 'relative';
          this.triggerBtn.appendChild(newBadge);
        }
      } else if (badge) {
        badge.remove();
      }
    }
  }

  private showReplyNotification(item: FeedbackSummary): void {
    const toast = document.createElement('div');
    toast.style.cssText =
      'position:fixed;bottom:20px;right:20px;padding:12px 16px;background:var(--fh-primary);color:white;border-radius:8px;font-size:13px;box-shadow:0 2px 8px rgba(0,0,0,0.15);z-index:10001;max-width:300px';
    toast.textContent = `New reply on: ${escapeHtml(item.title)}`;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transition = 'opacity 0.3s';
      setTimeout(() => toast.remove(), 300);
    }, 4000);
  }

  // ─── Helpers ──────────────────────────────────────────────────────────

}
